import { TenantConfig } from './bindings.types';
import { FlowStatus, zPrintSize } from './general';
import { Perspective } from './literals';
import { z } from 'zod';
import { SectionView } from './confdefnComponents';
import { filterInvalidItem, formatAndLogLocal, validateAtLevel } from './confdefnFilter';
import _ from 'lodash';
import {
  FabType,
  zClientDataApi,
  zConfigApi,
  zDataApi,
  HasActionButtonsMenu,
  defaultWorklistButtons,
} from './confdefnView';
import { o_BaseDefnsComponentProps } from './confdefnComponentProps';

// Confdefn tab and section structure validators

const Section = z.object({
  id: z.string(),
  name: z.string(),
  icon: z.string(),
  pathSlot: z.string(),
  inPerspectives: z.array(Perspective),
  tooltip: z.string().optional(),
  /** Available views to display under section in LeftNav */
  views: z.array(SectionView),
  defaultView: z.string().optional(),
  disabled: z.boolean().optional(),
  hidden: z.boolean().optional(),
});

export const LeftNavSection = Section;

const DisabledTab = z.object({
  id: z.string(),
  name: z.string(),
  pathSlot: z.string(),
  disabled: z.literal(true),
  inPerspectives: z.array(Perspective),
});

const o_ContextButton = {
  text: z.string().optional(),
  tooltip: z.string().optional(),
  inPerspectives: z.array(z.string()).optional().default([]),
};
const zContextButton = z.object(o_ContextButton);

const zEditStyleButton = z.object({
  ...o_ContextButton,
  path: z.string(),
});

const zCartButton = z.object({
  ...o_ContextButton,
  iconSrc: z.string().optional().default('far fa-cart-arrow-down'),
  text: z.string().optional().default('Cart'),
});

// using `.and` instead because zDataApi is union type and `.merge` doesn't exist on that type
const zPlanBtn = zContextButton.and(zDataApi);

const zContextMfpModule = z.object({
  id: z.string(),
  pathSlot: z.string(),
  name: z.string(),
  siloId: z.string(),
  onlyIndirectMfpScoping: z.boolean().optional().default(false),
});

const zContextBookmarks = z.object({
  tabs: z.array(z.string()),
});

const o_QuickRecon = {
  ...o_BaseDefnsComponentProps(''),
  configApi: zConfigApi,
};
const zQuickReconButton = z.object({
  ...o_ContextButton,
  ...o_QuickRecon,
});

export interface ContextMfpModule extends z.infer<typeof zContextMfpModule> {}
export interface ContextBookmarks extends z.infer<typeof zContextBookmarks> {}
export interface ContextButton extends z.infer<typeof zContextButton> {}
export interface EditStyleButton extends z.infer<typeof zEditStyleButton> {}
export interface CartButton extends z.infer<typeof zCartButton> {}
export interface QuickReconButton extends z.infer<typeof zQuickReconButton> {}

/* TODO: if this continues to grow, should we consider top level props mfp/asst?
{
  mfp: { module: ... },
  asst: { tabs: ..., undoBtn: ...}
}
*/
const zContext = z.object({
  tabs: z.array(z.string()),
  undoBtn: zContextButton.optional(),
  editStyleBtn: zEditStyleButton.optional(),
  cartBtn: zCartButton.optional(),
  planBtn: zPlanBtn.optional(),
  quickReconBtn: zQuickReconButton.optional(),
  mfpModule: zContextMfpModule.optional(),
  bookmarks: zContextBookmarks.optional(),
  worklistConfigButtons: z.array(HasActionButtonsMenu).optional().default(defaultWorklistButtons),
  /**
   * A better name for this would be `filterContextKey`
   *
   * Used to tell the filters if it should send in something other than the context key name */
  alternateContextKey: z.string().optional(),
  filters: z
    .object({
      disabled: z.boolean(),
    })
    .optional(),
});

const zAppContext = z.record(zContext);

export interface Context extends z.infer<typeof zContext> {}
export interface AppContext extends z.infer<typeof zAppContext> {}

export const EnabledTab = z.object({
  id: z.string(),
  /** Determines if tab is enabled in the UI */
  disabled: z.literal(false),
  /** Selected section after tab loads */
  defaultSection: z.string(),
  /** Dash separated value used to generate the url of the selected tab  */
  pathSlot: z.string(),
  /** Sections to render in the LeftNav component */
  leftNavSections: z.array(LeftNavSection),
  /** Name of tab */
  name: z.string(),
  /** Perspectives of a tab */
  inPerspectives: z.array(Perspective),
  hidden: z.boolean().optional(),
  flowStatusOverride: FlowStatus.optional(),
});
export const isEnabledTab = (tab: z.infer<typeof ConfDefnTab>): tab is z.infer<typeof EnabledTab> => {
  return !tab.disabled;
};

export const ConfDefnTab = z.union([EnabledTab, DisabledTab]);

const ScopeSelectorType = z.enum(['AssortmentScopeSelector', 'MfpScopeSelector']);
const ScopeWorkflow = z.enum(['in-season', 'pre-season']);
const HeaderbarType = z.enum(['AssortmentHeaderbar', 'MfpHeaderbar']);
export const DependentScopeType = z.enum(['MfpScope']);

const zDependentScopes = z.object({
  type: DependentScopeType,
});

// TODO: see about combining, using z.union on cart/worklist makes discriminatedUnion unhappy
export const zFabTypeBase = z.object({
  fabType: z.string(),
  fabTooltip: z.string().optional(),
});

// Define individual fab types with specific properties
export const zCartFabType = zFabTypeBase.extend({
  fabType: z.literal(FabType.cart),
});

export const zWorklistFabType = zFabTypeBase.extend({
  fabType: z.literal(FabType.worklist),
});

export const zFabTypePlanning = zFabTypeBase.extend({
  fabType: z.literal(FabType.planning),
  dataApi: zPlanBtn.optional(),
  defnId: z.string().optional(),
});

export const zFabTypeButtonModal = zFabTypeBase.extend({
  fabType: z.literal(FabType.buttonModal),
  configApi: zConfigApi.optional(),
});

export const zPostTextModalFab = zFabTypeBase.extend({
  fabType: z.literal(FabType.postTextModal),
  text: z.string(),
  dataApi: zClientDataApi.optional(),
});

export const zFabTypePivot = zFabTypeBase.extend({
  fabType: z.literal(FabType.pivot),
  fabIcon: z.string(),
  dataApi: zPlanBtn,
  toastError: z.boolean().optional().default(true),
  showRefreshFab: z.boolean().optional().default(false),
});

export interface CartFabType extends z.infer<typeof zCartFabType> {}
export interface WorklistFabType extends z.infer<typeof zWorklistFabType> {}
export interface FabTypePivot extends z.infer<typeof zFabTypePivot> {}
export interface FabTypePlanning extends z.infer<typeof zFabTypePlanning> {}
export interface FabTypeButtonModal extends z.infer<typeof zFabTypeButtonModal> {}
export interface PostTextModalFab extends z.infer<typeof zPostTextModalFab> {}
export const zFabTypeUnion = z.discriminatedUnion('fabType', [
  zCartFabType,
  zWorklistFabType,
  zFabTypePlanning,
  zFabTypeButtonModal,
  zPostTextModalFab,
  zFabTypePivot,
]) as z.ZodType<CartFabType | WorklistFabType | FabTypePlanning | FabTypeButtonModal | PostTextModalFab | FabTypePivot>;
const zFabTypeRecord = z.record(z.string(), zFabTypeUnion);
export const AssortmentConfDefn = z.object({
  /** The type of project the configs are setup for */
  id: z.union([z.literal('Assortment'), z.literal('Configurator'), z.literal('_empty_')]),
  /** The selected tab after login and perspective selection */
  defaultTab: z.string(),
  version: z.number().optional(),
  schema: z.string().optional(),
  buttons: z.any().describe('This property is no longer used and can be removed'),
  canAddNewStyles: z.boolean().optional().default(true),
  showBookmarks: z.boolean().optional().default(false),
  /** Values shown for flowStatus and initial selections  */
  flowStatus: FlowStatus.default({ values: [], defaultSelection: [] }),
  /** Placeholder config for changing placeholder text, etc. */
  placeholder: z
    .object({
      text: z.string(),
    })
    .default({
      text: 'PLACEHOLDER',
    }),
  /** i.e. YTD, QTD, MTD, etc. */
  lookBackPeriods: z.array(z.string()).default([]),
  /** Available Perspectives */
  perspectives: z.array(Perspective),
  perspective: z
    .object({
      scopeConfig: z.object({
        type: ScopeSelectorType,
        workflow: ScopeWorkflow.optional(),
        dependentScopes: z.array(zDependentScopes).default([]).optional(),
      }),
      headerbar: z.object({
        type: HeaderbarType,
      }),
    })
    .default({
      scopeConfig: {
        type: 'AssortmentScopeSelector',
        dependentScopes: [],
      },
      headerbar: {
        type: 'AssortmentHeaderbar',
      },
    })
    .optional(),
  /** For Filters/Style Pane to map tabs to a predetermined application context */
  context: zAppContext.default({
    assortment: {
      tabs: [],
    },
    hindsighting: {
      tabs: [],
    },
    allocation: {
      tabs: [],
    },
  }),
  /**
    Tabs to render in TopNavBar, containing Section and View configurations.
    Reporting Tab can actually be component itself so LeftNavSection is necessary in union
  */
  tabs: z.array(ConfDefnTab),
  /** Available print dimensions */
  printSizes: z.array(zPrintSize).default([
    { width: 8.5, height: 11 },
    { width: 11, height: 8.5 },
    { width: 17, height: 11 },
    { width: 16.54, height: 23.39, name: 'A2' },
    { width: 11.7, height: 16.5, name: 'A3' },
    { width: 8.3, height: 11.7, name: 'A4' },
  ]),
  /** For views like configuration */
  disableScope: z.boolean().optional().default(false),
  disableFilters: z.boolean().optional().default(false),
  fabTypes: zFabTypeRecord,
});

export interface UIConfDefn extends z.infer<typeof AssortmentConfDefn> {}
/**
 * Validate each level of the confdefn tree, filtering out invalids items.
 * An invalid item at a higher level removes that level and its children.
 *
 * Current levels:
 * - UIConfDefn level (minus the tabs)
 * - Tabs level (minus the sections)
 * - Sections level (minus the views)
 * - Views level (all views)
 *
 * @param {UIConfDefn} input - the unvalidated confdefn
 *
 * @returns {TenantConfig} a validated/filtered confdefn
 */
export function validateConfDefn(input: UIConfDefn, loggingService: any, alwaysThrowOnInvalid = false): TenantConfig {
  let errors: string[] = [];

  // Require valid top level letting error propagate to catch if invalid
  const uiConfDefnLevel = validateAtLevel(AssortmentConfDefn, input, {
    childLevel: 'tabs',
    throwOnInvalid: alwaysThrowOnInvalid || true,
  });

  // Validate at each level (tab, section, view)
  const childLevels = _.map(input.tabs, (tab, tabIndex) => {
    // validate disabled tab
    if (tab.disabled) {
      const validatedDisabledTab = validateAtLevel(DisabledTab, tab, { throwOnInvalid: alwaysThrowOnInvalid });
      if (_.isNil(validatedDisabledTab)) {
        const error = `Invalid tab item [${tab.id}] detected at position [${tabIndex}] in 'tabs' array.`;
        errors = _.concat(errors, error);
        return;
      }

      return validatedDisabledTab;
    }

    // validate enabled tab and reinsert tab children after validation
    const validatedTabLevel = validateAtLevel(EnabledTab, tab, {
      childLevel: 'leftNavSections',
      throwOnInvalid: alwaysThrowOnInvalid,
    });
    if (_.isNil(validatedTabLevel)) {
      const error = `Invalid tab item [${tab.id}] detected at position [${tabIndex}] in 'tabs' array.`;
      errors = _.concat(errors, error);
      return;
    }

    const validatedTabLevelWithChildren = {
      ...validatedTabLevel,
      leftNavSections: tab.leftNavSections,
    };

    // validate all sections in tab
    const validatedSectionLevels = _.map(validatedTabLevelWithChildren.leftNavSections, (section, sectionIndex) => {
      // validate section and reinsert section children after validation
      const validatedSectionLevel = validateAtLevel(LeftNavSection, section, {
        childLevel: 'views',
        throwOnInvalid: alwaysThrowOnInvalid,
      });
      if (_.isNil(validatedSectionLevel)) {
        const error = `Invalid section item [${section.id}] detected in 'leftNavSections' array at tab position [${tabIndex}] -> leftNavSections position [${sectionIndex}].`;
        errors = _.concat(errors, error);
        return;
      }

      const validatedSectionLevelWithChildren = {
        ...validatedSectionLevel,
        views: section.views,
      };

      // validate all views in section
      const validatedViewLevels = _.map(validatedSectionLevelWithChildren.views, (view, viewIndex) => {
        const validatedViewLevel = validateAtLevel(SectionView, view, { throwOnInvalid: alwaysThrowOnInvalid });
        if (_.isNil(validatedViewLevel)) {
          const error = `Invalid view item [${view.id}] detected in 'views' array at tabs position [${tabIndex}] -> leftNavSections position [${sectionIndex}] -> views position [${viewIndex}].`;
          errors = _.concat(errors, error);
          view.errorView = true;
          return view;
        }

        return validatedViewLevel;
      }).filter(filterInvalidItem);

      return {
        ...validatedSectionLevel,
        views: validatedViewLevels,
      };
    }).filter(filterInvalidItem); // filter out invalid (undefined) section(s)

    return {
      ...validatedTabLevel,
      leftNavSections: validatedSectionLevels,
    };
  }).filter((tab) => !_.isNil(tab)); // filter out invalid (undefined) tab(s)

  if (!_.isEmpty(errors)) {
    const errorMessage = 'INVALID UI CONFDEFN ITEMS DETECTED:';
    // true skips the logging to browser console
    loggingService.warn(errorMessage, errors.join('\n'), true);
    formatAndLogLocal(errorMessage, errors);
  }

  // validated/filtered result
  return {
    ...uiConfDefnLevel,
    tabs: childLevels,
  };
}
